import { call, put, takeLatest } from 'redux-saga/effects';
import {
  CASE_FETCH_FILE_PIPELINE_STATUS_BEGIN,
  CASE_FETCH_FILE_PIPELINE_STATUS_SUCCESS,
  CASE_FETCH_FILE_PIPELINE_STATUS_FAILURE,
} from './constants';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

export const fetchFilePipelineStatus = createPromiseAction(
  CASE_FETCH_FILE_PIPELINE_STATUS_BEGIN,
);

export function* doFetchFilePipelineStatus(action) {
  const {
    payload: { caseId, fileId },
  } = action;

  try {
    const res = yield call(api.get, `/cases/${caseId}/files/${fileId}/pipeline-status`);

    if (res && res.error) {
      yield put({
        type: CASE_FETCH_FILE_PIPELINE_STATUS_FAILURE,
        error: res.error,
        meta: { fileId },
      });
      return yield call(rejectPromiseAction, action, res.error);
    }

    yield put({
      type: CASE_FETCH_FILE_PIPELINE_STATUS_SUCCESS,
      data: Array.isArray(res) ? res : [],
      meta: { fileId },
    });
    yield call(resolvePromiseAction, action, res);
  } catch (error) {
    yield put({
      type: CASE_FETCH_FILE_PIPELINE_STATUS_FAILURE,
      error,
      meta: { fileId },
    });
    yield call(rejectPromiseAction, action, error);
  }
}

export function* watchFetchFilePipelineStatus() {
  yield takeLatest(fetchFilePipelineStatus, withCurrentCaseId(doFetchFilePipelineStatus));
}

export function useFetchFilePipelineStatus() {
  const dispatch = useDispatch();

  const {
    filePipelineStatus,
    fetchFilePipelineStatusPending,
    filePipelineStatusError,
  } = useSelector(
    (state) => ({
      filePipelineStatus: state.case.filePipelineStatus,
      fetchFilePipelineStatusPending: state.case.fetchFilePipelineStatusPending,
      filePipelineStatusError: state.case.filePipelineStatusError,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (fileId) => {
      return dispatch(fetchFilePipelineStatus({ fileId }));
    },
    [dispatch],
  );

  return {
    filePipelineStatus,
    fetchFilePipelineStatusPending,
    filePipelineStatusError,
    fetchFilePipelineStatus: boundAction,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_FILE_PIPELINE_STATUS_BEGIN + '.TRIGGER': {
      const { fileId } = action.payload || {};
      if (!fileId) {
        return state;
      }
      return {
        ...state,
        fetchFilePipelineStatusPending: {
          ...state.fetchFilePipelineStatusPending,
          [fileId]: true,
        },
        filePipelineStatusError: {
          ...state.filePipelineStatusError,
          [fileId]: null,
        },
      };
    }

    case CASE_FETCH_FILE_PIPELINE_STATUS_SUCCESS: {
      const {
        meta: { fileId },
        data,
      } = action;
      return {
        ...state,
        fetchFilePipelineStatusPending: {
          ...state.fetchFilePipelineStatusPending,
          [fileId]: false,
        },
        filePipelineStatus: {
          ...state.filePipelineStatus,
          [fileId]: data,
        },
        filePipelineStatusError: {
          ...state.filePipelineStatusError,
          [fileId]: null,
        },
      };
    }

    case CASE_FETCH_FILE_PIPELINE_STATUS_FAILURE: {
      const {
        meta: { fileId },
        error,
      } = action;
      const errorMessage =
        (error &&
          (error.message
            ? error.message
            : typeof error === 'string'
              ? error
              : error && error.toString
                ? error.toString()
                : '')) ||
        'Unable to load pipeline status';
      return {
        ...state,
        fetchFilePipelineStatusPending: {
          ...state.fetchFilePipelineStatusPending,
          [fileId]: false,
        },
        filePipelineStatusError: {
          ...state.filePipelineStatusError,
          [fileId]: errorMessage || 'Unable to load pipeline status',
        },
      };
    }

    default:
      return state;
  }
}
